import { ITrack } from "@/interfaces/ITrack.ts"
import {
  getZoomview, PeaksInstanceType, WAVEFORM_ZOOMVIEW_TYPE_NAME, zoomFull, zoomIn, zoomOut,
} from "@/features/AudioWorkstation/utils/audio-editor-utils.ts"
import { RefObject } from "react"
import { PeaksInstance, SegmentOptions, WaveformZoomView, } from "peaks.js"
import { EventTypes, PointCueEvent, Segment, SegmentCueEvent } from "@/types/types.ts"
import { emitEvent, Emitter } from "@/services/event-emitter.ts"
import { RGBAToHex } from "@/utils/color-utils.ts"
import { arrowDownTag } from "@/services/peaks-js/CustomSegmentMarker.ts";
import Konva from "konva"
import { MouseDragHandlerExtension } from "@/features/AudioWorkstation/services/MouseDragHandlerExtension.ts";
import { jotaiStore } from "@/state/jotai/jotaiStore.ts";
import { editingSegmentAtom } from "@/state/jotai/atom/editingSegmentAtom.ts";
import { peaksDataLoadedAtom } from "@/state/jotai/atom/peaksDataLoadedAtom.ts";
import { audioEditorPeaksInstanceAtom } from "@/state/jotai/atom/audioEditorPeaksInstanceAtom.ts";
import { getCssVariable } from "@/utils/utils.ts";
import { PlaymodeType } from "@/enum/playmodeType.ts";
import { fetchNextSegment } from "@/services/api/track.ts";
import { playingSegmentAtom } from "@/state/jotai/atom/playingSegmentAtom.ts";
import { AudioAdapter } from "@/features/AudioWorkstation/adapters/AudioAdapter.ts"
import Group = Konva.Group;

class AudioEditor {
  private _track: ITrack | null
  private _peaksInstance: PeaksInstanceType | null

  constructor() {
    this._audioAdapter = null
    this._uniqueTabId = null
    this._jsonWebToken = null
    this._track = null
    this._playmode = null
    this._peaksInstance = null
    this._zoomviewContainerRef = null
    this._overviewContainerRef = null
  }

  private _audioAdapter: AudioAdapter | null

  get audioAdapter(): AudioAdapter | null {
    return this._audioAdapter;
  }

  private _uniqueTabId: string | null

  get uniqueTabId(): string | null {
    return this._uniqueTabId;
  }

  private _jsonWebToken: string | null

  get jsonWebToken(): string | null {
    return this._jsonWebToken;
  }

  private _overviewContainerRef: RefObject<HTMLDivElement | null> | null

  get overviewContainerRef(): RefObject<HTMLDivElement | null> | null {
    return this._overviewContainerRef
  }

  private _zoomviewContainerRef: RefObject<HTMLDivElement | null> | null

  get zoomviewContainerRef(): RefObject<HTMLDivElement | null> | null {
    return this._zoomviewContainerRef
  }

  private _playmode: PlaymodeType | null

  get playmode(): PlaymodeType | null {
    return this._playmode;
  }

  setAudioAdapter(value: AudioAdapter | null) {
    this._audioAdapter = value;
    return this
  }

  setTrack = (track: ITrack | null) => {
    this._track = track

    return this
  }

  getTrack = (): (ITrack | null) => {
    return this._track
  };

  setUniqueTabId(value: string | null) {
    this._uniqueTabId = value;

    return this;
  }

  setJsonWebToken(value: string | null) {
    this._jsonWebToken = value;

    return this;
  }

  addPeaksSegmentCueListeners() {
    this._peaksInstance?.on("segments.exit", this.onSegmentExit)
    this._peaksInstance?.on("segments.enter", this.onSegmentEnter);

    this._peaksInstance?.on("points.enter", this.onPointEnter);

    return this
  }

  removePeaksSegmentCueListeners() {
    // console.log('removePeaksSegmentCueListeners')
    this._peaksInstance?.off("segments.exit", this.onSegmentExit)
    this._peaksInstance?.off("segments.enter", this.onSegmentEnter);
    return this
  }

  addEventEmitterEvents = () => {
    Emitter
      .on(EventTypes.ZOOM_FULL, this.onZoomFull)
      .on(EventTypes.ZOOM_IN, this.onZoomIn)
      .on(EventTypes.ZOOM_OUT, this.onZoomOut)

    return this
  }

  /*removeEventEmitterEvents = () => {
    Emitter
      .off(EventTypes.ZOOM_FULL, this.onZoomFull)
      .off(EventTypes.ZOOM_IN, this.onZoomIn)
      .off(EventTypes.ZOOM_OUT, this.onZoomOut)

    return this
  }*/

  destroy = () => {
    // console.log('audio-editor.ts: destroy')

    jotaiStore.set(peaksDataLoadedAtom, false)
    jotaiStore.set(audioEditorPeaksInstanceAtom, null)

    this
      .setTrack(null)
      .removeZoomviewEventListeners()
      .removeOverviewEventListener()
      .removePeaksSegmentCueListeners()

    this._peaksInstance?.destroy()
    return this
  }

  addOverviewEventListener = (overviewContainerRef: RefObject<HTMLDivElement | null>) => {
    this._overviewContainerRef = overviewContainerRef
    overviewContainerRef.current?.addEventListener("mousemove", this.onOverviewMousemove, {
      passive: false,
    })

    return this
  }

  removeOverviewEventListener = () => {
    this.overviewContainerRef?.current?.removeEventListener("mousemove", this.onOverviewMousemove)

    return this
  }

  handleZoomviewMouseLeave = () => {
    audioEditor.setSegmentDefaultColor()
  }

  addZoomviewEventListener = (zoomviewContainerRef: RefObject<HTMLDivElement | null>) => {
    this._zoomviewContainerRef = zoomviewContainerRef

    zoomviewContainerRef.current?.addEventListener("mousemove", this.onZoomviewMousemove, {
      passive: false,
    })

    zoomviewContainerRef.current?.addEventListener('mouseleave', this.handleZoomviewMouseLeave)

    return this
  }

  removeZoomviewEventListeners = () => {
    this.zoomviewContainerRef?.current?.removeEventListener('mouseleave', this.handleZoomviewMouseLeave)

    return this
  }

  onZoomFull = () => {
    zoomFull(this._peaksInstance as PeaksInstance)
  }

  onZoomIn = () => {
    zoomIn(this._peaksInstance as PeaksInstanceType)
  }

  onZoomOut = () => {
    zoomOut(this._peaksInstance as PeaksInstanceType)
  }

  updatePointerOffsetAndTime = () => {

  }

  onOverviewMousemove = () => {
    this.updatePointerOffsetAndTime()
  }

  setPeaksInstance = (peaks: PeaksInstanceType) => {
    this._peaksInstance = peaks

    return this
  }

  onZoomviewMousemove = () => {
    this.updatePointerOffsetAndTime()
  }

  setSegmentDefaultColor = () => {
    const segment = this.getEditingSegment() as Segment
    if (!segment) {
      return
    }

    const segmentColor = segment?.color as string
    if (segmentColor.indexOf("rgba") === -1) {
      return
    }

    segment.update({
      color: RGBAToHex(segment.color as string),
    } as SegmentOptions)
  }

  /*setSegmentDraggingColor = () => {
    const segment = this.getEditingSegment() as Segment
    if (!segment) {
      return
    }
    const segmentColor = segment.color as string
    if (segmentColor.indexOf("rgba") !== -1) {
      return
    }

    segment.update({
      color: hexToRGBA(segment.color as string, 0.5),
    } as SegmentOptions)
  }*/

  onSegmentEnter = (event: SegmentCueEvent) => {
    const { segment } = event

    jotaiStore.set(playingSegmentAtom, segment)
  }

  onPointEnter = (event: PointCueEvent) => {
    emitEvent(EventTypes.CUE_POINT_MESSAGE, { point: event.point, event })
  }

  onSegmentExit = (event: SegmentCueEvent) => {
    const segment = event.segment
    const roundedEventTime = parseFloat(event.time.toFixed(1));
    const roundedSegmentTime = parseFloat(segment.endTime.toFixed(1));

    if (roundedEventTime === roundedSegmentTime && this.playmode === PlaymodeType.SEGMENTS) {
      this.audioAdapter?.pause();
      fetchNextSegment(
        this.getTrack()?.explorerItemId || '',
        this.audioAdapter?.getCurrentTime() || 0,
      ).then(nextSegment => {
        this.audioAdapter
          ?.seek(nextSegment.startTime)
          ?.play()
      })
    }

    jotaiStore.set(playingSegmentAtom, null)
  }

  enableAutoScroll = (followPlayheadModeIsEnabled: boolean) => {
    const peaksInstance = this._peaksInstance
    if (!peaksInstance) {
      return
    }

    const views = peaksInstance.views
    if (!views) {
      throw new Error("Warning: peaks.views is not defined.")
    }

    const zoomview = views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME)
    if (!zoomview) {
      throw new Error("Warning: peaks.views.zoomview is not defined.")
    }

    zoomview.enableAutoScroll(followPlayheadModeIsEnabled, {})

    return this
  }

  getZoomview = (): WaveformZoomView => {
    return this._peaksInstance
      ?.views
      .getView(WAVEFORM_ZOOMVIEW_TYPE_NAME) as WaveformZoomView
  }

  addDragHandlerExtension(peaks: PeaksInstanceType, containerRef: RefObject<HTMLDivElement | null>) {
    const overviewMouseDragHandlerExtension = new MouseDragHandlerExtension(
      peaks,
      containerRef,
    )

    try {
      overviewMouseDragHandlerExtension.addEventEmitterListeners()
      overviewMouseDragHandlerExtension.addPeaksEventListeners()
      overviewMouseDragHandlerExtension.addWindowEventListeners()
    } catch (error) {
      console.warn(error)
    }

    return this
  }

  addCustomPlayheadStyle(peaks: PeaksInstanceType) {
    const zoomview = getZoomview(peaks)
    const playheadLayer = zoomview._playheadLayer
    const id = "playhead-indicator"
    const layer = playheadLayer._playheadLayer
    const group = layer.children?.[0] as Group
    const triangle = arrowDownTag(id, getCssVariable('--mui-palette-primary-main'))

    triangle.on('mouseenter', () => {
      emitEvent(EventTypes.SET_DOCUMENT_CURSOR, "ew-resize")
    })

    triangle.on('mouseleave', () => {
      emitEvent(EventTypes.UNSET_DOCUMENT_CURSOR)
    })

    // playheadLayer._playheadText.offsetY(-55)
    playheadLayer._playheadText.offsetX(-10)

    group.add(triangle)

    // modify playhead width
    playheadLayer._playheadLine.attrs.strokeWidth = 2

    zoomview._playheadLayer = playheadLayer

    return this
  }

  /*getAmplitudeScale() {
    return jotaiStore.get(amplitudeScaleAtom)
  }*/

  getEditingSegment = () => {
    return jotaiStore.get(editingSegmentAtom)
  }

  setPlaymode(playmode: PlaymodeType) {
    this._playmode = playmode

    return this
  }
}

export const audioEditor = new AudioEditor()
